import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";

import * as Yup from "yup";
import { useFormik } from "formik";

import {
  AppSettingsDto,
  UpdateAppSettingsRequest,
} from "../../interfaces/app-settings";
import { ParsedError, State } from "../../interfaces";
import {
  Alert,
  Card,
  CardBody,
  CardHeader,
  Col,
  Form,
  Label,
  Row,
} from "reactstrap";
import { AppSettingsTypeEnum, ErrorHandlingTypeEnum } from "../../enums";
import { ActivityButton, AppRichEditor } from "../../components/Common";
import {
  useGetAppSettingsQuery,
  useUpdateAppSettingsMutation,
} from "../../store/api/appSettingsApi";
import { toast } from "react-toastify";

export const ExamNoticePage = () => {
  //#region hooks
  const { t } = useTranslation();
  //#endregion

  document.title = `${t("documentTitle", {
    pageName: "controlAppExamNotice",
  })}`;

  //#region internal state
  const [request] = useState<AppSettingsDto>();
  //#endregion

  //#region api
  const {
    data: appSettings,
    isLoading: isLoadingAppSettings,
    isError: isErrorAppSettings,
  } = useGetAppSettingsQuery(AppSettingsTypeEnum.NOTICE_EXAM);

  const [
    updateAppSettings,
    {
      isLoading: isLoadingUpdate,
      isSuccess: isSuccessUpdate,
      error: errorUpdate,
      isError: isErrorUpdate,
    },
  ] = useUpdateAppSettingsMutation();
  //#endregion

  //#region form
  const form = useFormik<UpdateAppSettingsRequest>({
    enableReinitialize: true,
    initialValues: {
      value: {
        en: request?.value.en || "",
        ar: request?.value.ar || "",
      },
    },
    validationSchema: Yup.object({
      value: Yup.object({
        en: Yup.string().required(
          t("isNotEmpty", {
            displayName: "englishValue",
          }),
        ),
        ar: Yup.string().required(
          t("isNotEmpty", {
            displayName: "arabicValue",
          }),
        ),
      }),
    }),
    onSubmit: (values) => {
      updateAppSettings({
        type: AppSettingsTypeEnum.NOTICE_EXAM,
        data: values,
      });
    },
  });
  //#endregion

  //#region effects

  useEffect(() => {
    if (appSettings) {
      form.setValues({
        value: appSettings.value,
      });
    }
  }, [appSettings, isLoadingAppSettings]);

  useEffect(() => {
    if (!isLoadingUpdate && isSuccessUpdate) {
      toast.success(t("savedSuccessfully"), {
        autoClose: 3000,
      });
    }
  }, [isLoadingUpdate, isSuccessUpdate]);

  useEffect(() => {
    let _error: ParsedError | null = null;
    if (!isLoadingUpdate && isErrorUpdate) _error = errorUpdate as ParsedError;
    if (_error?.type == ErrorHandlingTypeEnum.FORM) {
      form.setErrors(_error.error);
    }
  }, [isLoadingUpdate]);

  //#endregion

  return (
    <Row>
      <Col>
        <Card>
          <CardHeader>
            <h6 className="card-title mb-0">{t("examNotice")}</h6>
          </CardHeader>
          <CardBody>
            <Form
              onSubmit={(e) => {
                e.preventDefault();
                form.handleSubmit();
                return false;
              }}
              action="#"
            >
              <Row>
                <Col className="col-sm-12 col-md-12 col-lg-6 col-12">
                  <div className="mb-3">
                    <Label htmlFor="value.en" className="form-label">
                      {t("valueEn")}
                      <span className="text-danger">*</span>
                    </Label>
                    <AppRichEditor
                      initialValue={appSettings?.value?.en || ""}
                      onChange={(data: any) => {
                        form.setFieldValue("value.en", data);
                      }}
                      onKeyUp={(data: any) => {
                        if (data.trim().length == 0) {
                          form.setFieldError(
                            "value.en",
                            t("isNotEmpty", {
                              displayName: "englishValue",
                            }),
                          );
                        } else {
                          form.setFieldError("value.en", "");
                        }
                      }}
                      onFocus={() => {
                        form.setFieldTouched("value.en", true, true);
                      }}
                    />
                    {form.touched.value?.en && form.errors.value?.en ? (
                      <Label className="invalid-feedback d-block">
                        {form.errors.value?.en as string}
                      </Label>
                    ) : null}
                  </div>
                </Col>
                <Col>
                  <div className="mb-3">
                    <Label htmlFor="value.ar" className="form-label">
                      {t("valueAr")}
                      <span className="text-danger">*</span>
                    </Label>
                    <AppRichEditor
                      initialValue={appSettings?.value?.ar || ""}
                      onChange={(data: any) => {
                        form.setFieldValue("value.ar", data);
                      }}
                      onKeyUp={(data: any) => {
                        if (data.trim().length == 0) {
                          form.setFieldError(
                            "value.ar",
                            t("isNotEmpty", {
                              displayName: "arabicValue",
                            }),
                          );
                        } else {
                          form.setFieldError("value.ar", "");
                        }
                      }}
                      onFocus={() => {
                        form.setFieldTouched("value.ar", true, true);
                      }}
                    />
                    {form.touched.value?.ar && form.errors.value?.ar ? (
                      <Label className="invalid-feedback d-block">
                        {form.errors.value?.ar as string}
                      </Label>
                    ) : null}
                  </div>
                </Col>
              </Row>
              <Row>
                <Col
                  style={{
                    display: "flex",
                    justifyContent: "end",
                    alignItems: "center",
                  }}
                >
                  <ActivityButton
                    title="save"
                    inProgressTitle="saving"
                    color="success"
                    className="btn btn-success"
                    type="submit"
                    inProgress={isLoadingAppSettings || isLoadingUpdate}
                    disabled={isLoadingAppSettings || isLoadingUpdate}
                  ></ActivityButton>
                </Col>
              </Row>
            </Form>
          </CardBody>
        </Card>
      </Col>
    </Row>
  );
};
